import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Empty, SpinLoading } from "antd-mobile";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import {
  OrderCard,
  SelectPayTypeModal,
  QuantityFilterModal,
  TopRightMenuModal,
  EPayType,
} from "../../components/Wallet";
import useGlobalTheme from "../../hooks/useGlobalTheme";

const menuIcon = require("../../assets/images/top-left-menu.jpg");
const bankImg = require("../../assets/images/wallet/bank.png");
const alipayImg = require("../../assets/images/wallet/alipay.png");
const wechatImg = require("../../assets/images/wallet/wechat.png");

const ScrollArea = styled.div`
  height: calc(100% - ${headerHeight});
  overflow: auto;
  background: #f7f8ff;
  position: relative;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const FilterItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  margin-right: 12px;
  font-size: 12px;
  cursor: pointer;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.secondaryText || "#999"};
`;

const MenuBtn = styled.div`
  position: absolute;
  right: 12px;
  top: 10px;
  display: flex;
  align-items: center;
  font-size: 12px;
  cursor: pointer;
  z-index: 3;
`;

const Arrow = styled.span<{ $up?: boolean; $active?: boolean; $desc?: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 3px;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  ${({ $up, $active, $desc, theme }) =>
    $up
      ? `border-bottom: 5px solid ${
          $active && $desc ? theme.colors.primary : "#bbb"
        };`
      : `border-top: 5px solid ${
          $active && !$desc ? theme.colors.primary : "#bbb"
        };`}
`;

const SortArrows = ({
  active,
  desc,
  both,
}: {
  active: boolean;
  desc?: boolean;
  both?: boolean;
}) => (
  <span style={{ display: "inline-flex", flexDirection: "column", marginLeft: 2 }}>
    {both && <Arrow $up $active={active} $desc={!!desc} />}
    <Arrow $active={active} $desc={!!desc} />
  </span>
);

const payIcon = (t: number) =>
  t === EPayType.BANK ? bankImg : t === EPayType.ALIPAY ? alipayImg : wechatImg;

const BuyCoinList = () => {
  const theme = useGlobalTheme();
  const { orderStore, walletStore } = useStores();
  const [typePayload, setTypePayload] = useState([
    EPayType.BANK,
    EPayType.ALIPAY,
    EPayType.WECHAT,
  ]);
  const [filterParams, setFilterParams] = useState<number | undefined>();
  const [filterModalShow, setFilterModalShow] = useState(false);
  const [menuModalShow, setMenuModalShow] = useState(false);
  const [sortPayload, setSortPayload] = useState<any>(null);
  const [isPayTypeModalShow, setIsPayTypeModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const init = async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      await orderStore.loadOrderlist();
      await walletStore.getWalletInfo();
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const list = useMemo(() => {
    let ol = orderStore.orderList.slice();
    if (filterParams === 2) ol = ol.filter((item) => item.stock >= 100 && item.stock < 1000);
    else if (filterParams === 3)
      ol = ol.filter((item) => item.stock >= 1000 && item.stock < 3000);
    else if (filterParams === 4)
      ol = ol.filter((item) => item.stock >= 3000 && item.stock < 8000);
    else if (filterParams === 5)
      ol = ol.filter((item) => item.stock >= 8000 && item.stock < 12000);
    else if (filterParams === 6) ol = ol.filter((item) => item.stock >= 12000);

    ol = ol.filter(
      (a) => !!a.payment_methods?.filter((v) => typePayload.includes(v)).length
    );

    const key = sortPayload?.key;
    if (key && sortPayload.type === "sort") {
      const factor1 = sortPayload.desc ? -1 : 1;
      const factor2 = sortPayload.desc ? 1 : -1;
      ol = ol.slice().sort((a, b) => (a[key] > b[key] ? factor1 : factor2));
    }
    return ol;
  }, [orderStore.orderList, filterParams, typePayload, sortPayload]);

  const filterLabel =
    filterParams === 1
      ? "全部"
      : filterParams === 2
      ? "100-1千"
      : filterParams === 3
      ? "1千-3千"
      : filterParams === 4
      ? "3千-8千"
      : filterParams === 5
      ? "8千-1万2"
      : filterParams === 6
      ? "1万2以上"
      : "区间";

  const toggleSort = (key: string) => {
    setSortPayload((prev) => {
      if (prev?.key === key) return { ...prev, desc: !prev.desc };
      return { key, type: "sort", desc: true };
    });
  };

  return (
    <PageWrap>
      <PageHeader title="购买" />
      <ScrollArea>
        <MenuBtn onClick={() => setMenuModalShow(true)}>
          <img src={menuIcon} alt="" width={14} height={14} />
          <span style={{ marginLeft: 5 }}>菜单</span>
        </MenuBtn>
        <FilterBar>
          <FilterItem $active={!!typePayload.length} onClick={() => setIsPayTypeModalShow(true)}>
            {typePayload.map((item) => (
              <img
                key={item}
                src={payIcon(item)}
                alt=""
                width={15}
                height={15}
                style={{ margin: "0 2px", borderRadius: 3 }}
              />
            ))}
            <SortArrows active={!!typePayload.length} />
          </FilterItem>
          <FilterItem
            $active={sortPayload?.key === "quantities"}
            onClick={() => setFilterModalShow(true)}
          >
            {filterLabel}
            <SortArrows active />
          </FilterItem>
          <FilterItem
            $active={sortPayload?.key === "quantities"}
            onClick={() => toggleSort("quantities")}
          >
            数量
            <SortArrows
              active={sortPayload?.key === "quantities"}
              desc={sortPayload?.desc}
              both
            />
          </FilterItem>
          <FilterItem
            $active={sortPayload?.key === "amount"}
            onClick={() => toggleSort("amount")}
          >
            单价
            <SortArrows
              active={sortPayload?.key === "amount"}
              desc={sortPayload?.desc}
              both
            />
          </FilterItem>
        </FilterBar>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <SpinLoading color={theme.colors.primary} />
          </div>
        ) : list.length === 0 ? (
          <div onClick={isError ? init : undefined} style={{ cursor: isError ? "pointer" : "default" }}>
            <Empty
              description={isError ? "加载失败，点击重试" : "暂无数据"}
              style={{ padding: 40 }}
            />
          </div>
        ) : (
          list.map((order) => (
            <OrderCard order={order} key={order.order_no} />
          ))
        )}
      </ScrollArea>

      <SelectPayTypeModal
        title="支付方式"
        value={typePayload}
        visible={isPayTypeModalShow}
        setVisible={setIsPayTypeModalShow}
        onSelect={setTypePayload}
      />
      <QuantityFilterModal
        visible={filterModalShow}
        setVisible={setFilterModalShow}
        value={filterParams}
        onSelect={setFilterParams}
      />
      <TopRightMenuModal visible={menuModalShow} setVisible={setMenuModalShow} />
    </PageWrap>
  );
};

export default observer(BuyCoinList);
